import { Check, ChevronDown } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useAgentFace, useAgentName, useFacePicker } from './agentName'
import { PEOPLE, PEOPLE_ORDER, Person } from './contacts'

interface ContactStripProps {
  /** The person in the room alongside Bluey, or null for Bluey alone. */
  person: Person | null
  onSelect: (person: Person | null) => void
  /** An unread proactive message waiting on the agent. */
  unread?: boolean
  /** Drops the chip when the agent is alone — it would only repeat the brand. */
  hideAgentLabel?: boolean
  /** Who can be pulled in. Defaults to everyone the account knows; empty drops the control. */
  people?: Person[]
  /** The agent's face colour, where it is not Cai's own. */
  agentTint?: string
}

/** The agent's face: a rounded square, so it never reads as one of the people. */
function AgentFace({ unread, tint }: { unread?: boolean; tint?: string }) {
  const face = useAgentFace()
  const { options, choose } = useFacePicker()

  const avatar = (
    <span className="relative">
      <Avatar className="size-11 rounded-lg border-4 border-white shadow-md">
        {face ? (
          <AvatarImage src={face} alt="" className="scale-125 object-cover" />
        ) : (
          // Empty on purpose: the agent is a character, and none is drawn yet.
          <AvatarFallback className={cn('bg-brand-100 rounded-lg', tint)} />
        )}
      </Avatar>
      {unread && (
        <span
          className="bg-destructive absolute top-0 right-0 size-3 rounded-full border-2 border-white"
          aria-label="1 unread message"
          role="status"
        />
      )}
    </span>
  )

  if (!options?.length) return avatar

  return (
    <DropdownMenu>
      <DropdownMenuTrigger aria-label="Change the agent's character" className="rounded-lg">
        {avatar}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-48">
        <DropdownMenuLabel className="text-[10px]">Pick a character</DropdownMenuLabel>
        {options.map((option) => (
          <DropdownMenuItem key={option.name} onSelect={() => choose(option.src)} className="gap-2">
            <Avatar className="size-7 rounded-md">
              <AvatarImage src={option.src} alt="" className="object-cover" />
            </Avatar>
            <span className="text-ink truncate text-xs">{option.name}</span>
            {face === option.src && <Check className="text-brand-700 ml-auto size-3.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function PersonFace({ person, className }: { person: Person; className?: string }) {
  const { photo, initials, name } = PEOPLE[person]

  return (
    <Avatar className={cn('border-2 border-white shadow-sm', className)}>
      {photo && <AvatarImage src={photo} alt={name} className="object-cover" />}
      <AvatarFallback className="bg-ink-100 text-ink text-[10px] font-medium">{initials}</AvatarFallback>
    </Avatar>
  )
}

/**
 * Bluey holds the centre; a person joins beside it when picked. The people
 * stack small on the right as one control, so the agent stays the face of the
 * chat and the humans read as who you can pull into it.
 */
export function ContactStrip({
  person,
  onSelect,
  unread,
  hideAgentLabel,
  people = PEOPLE_ORDER,
  agentTint,
}: ContactStripProps) {
  const agentName = useAgentName()
  const agentFace = useAgentFace()
  const joined = person ? PEOPLE[person] : null

  return (
    <div
      className={cn(
        'relative flex items-center justify-center px-3 pt-2',
        hideAgentLabel && !joined ? 'pb-3' : 'pb-7'
      )}
    >
      <div className="flex items-center gap-2">
        {/* The joined person sits to Bluey's left, so the pair reads as a room. */}
        {person && <PersonFace person={person} className="size-11" />}
        <AgentFace unread={unread} tint={agentTint} />
      </div>

      {/* Hangs under whoever is in the room, naming them. */}
      {!(hideAgentLabel && !joined) && (
        <span className="absolute top-full left-1/2 z-20 flex -translate-x-1/2 -translate-y-6 items-center gap-1 whitespace-nowrap">
          <span className="text-ink rounded-full border border-white/60 bg-white/50 px-2 py-0.5 text-[10px] leading-none font-medium shadow-sm backdrop-blur-md">
            {joined ? `${joined.name} & ${agentName}` : agentName}
          </span>
        </span>
      )}

      {people.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Add someone to the chat"
            className="hover:bg-ink-50 absolute right-3 flex items-center gap-0.5 rounded-full p-1"
          >
            <span className="flex items-center">
              {people.map((entry, index) => (
                <PersonFace
                  key={entry}
                  person={entry}
                  className={cn('size-6', index > 0 && '-ml-2', person === entry && 'ring-brand-700 ring-2')}
                />
              ))}
            </span>
            <ChevronDown className="text-ink-300 size-3" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {/* The way back out of a room: the agent on its own, where every one of
                these conversations starts. */}
            <DropdownMenuItem onSelect={() => onSelect(null)} className="gap-2">
              <Avatar className="size-7 rounded-md">
                {agentFace ? (
                  <AvatarImage src={agentFace} alt="" className="object-cover" />
                ) : (
                  <AvatarFallback className="bg-brand-100 rounded-md" />
                )}
              </Avatar>
              <span className="flex min-w-0 flex-col">
                <span className="text-ink truncate text-xs font-medium">{agentName}</span>
                <span className="text-ink-400 truncate text-[10px]">AI for fleet owners</span>
              </span>
              {!person && <Check className="text-brand-700 ml-auto size-3.5" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {people.map((entry) => {
              const profile = PEOPLE[entry]
              const selected = person === entry
              return (
                <DropdownMenuItem
                  key={entry}
                  onSelect={() => onSelect(selected ? null : entry)}
                  className="gap-2"
                >
                  <PersonFace person={entry} className="size-7" />
                  <span className="flex min-w-0 flex-col">
                    <span className="text-ink truncate text-xs font-medium">{profile.name}</span>
                    <span className="text-ink-400 truncate text-[10px]">{profile.role}</span>
                  </span>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
